import { Draggable } from '@hello-pangea/dnd';
import {
  AccessTime,
  Business,
  CheckCircle as CheckCircleIcon,
  Delete,
  DirectionsCar,
  EventNote as EventNoteIcon,
  PersonOutline,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { darken, useTheme } from '@mui/material/styles';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import { memo, useMemo, type MouseEvent } from 'react';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import { useInventory } from 'hooks/inventory/useInventory';
import { usePermissions } from 'hooks/auth/usePermissions';
import { useInventoryAssignableUsers } from 'hooks/useUsers';
import {
  buildActiveInventoryVinSet,
  isVinInActiveInventory,
} from '../../util/activeInventoryVinSet';
import { resolveInventoryRepName } from '../../util/inventoryRepDisplay';

interface InventoryLeadCardProps {
  lead: InventoryLead;
  index: number;
  status: string;
  contactName: string;
  companyName: string;
  columnName?: string;
  columnColor?: string;
  onViewDetails?: (lead: InventoryLead) => void;
  onDelete?: () => void;
}

const toValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTimeDuration = (startDate: Date, endDate: Date = new Date()): string => {
  const days = differenceInDays(endDate, startDate);
  if (days > 0) return `${days}d`;
  const hours = differenceInHours(endDate, startDate);
  if (hours > 0) return `${hours}h`;
  const minutes = differenceInMinutes(endDate, startDate);
  return `${Math.max(minutes, 0)}m`;
};

/** Time since the lead's latest status-history entry that matches the current column. */
const getTimeInStatus = (lead: InventoryLead, columnStatus: string): string => {
  if (!lead.statusHistory || lead.statusHistory.length === 0) {
    const createdAt = toValidDate(lead.createdAt);
    return createdAt ? formatTimeDuration(createdAt) : '—';
  }
  const sorted = [...lead.statusHistory].sort(
    (a, b) => (toValidDate(b.changedAt)?.getTime() || 0) - (toValidDate(a.changedAt)?.getTime() || 0),
  );
  const match = sorted.find((entry) => entry.status === columnStatus);
  if (match) {
    const changedAt = toValidDate(match.changedAt);
    return changedAt ? formatTimeDuration(changedAt) : '—';
  }
  if (lead.status === columnStatus) return 'Just now';
  return '—';
};

/** e.g. "2020 Cascadia 26ft · +2" */
const formatInventoryTruckLabel = (truck: InventoryLead['trucks'][number]): string =>
  [truck.year || '', truck.model, truck.size].filter(Boolean).join(' ').trim() || 'Truck';

const getTruckSummary = (trucks: InventoryLead['trucks']): string => {
  if (!trucks || trucks.length === 0) return 'No trucks listed';
  const base = formatInventoryTruckLabel(trucks[0]);
  return trucks.length > 1 ? `${base} · +${trucks.length - 1}` : base;
};

const getFullTruckList = (trucks: InventoryLead['trucks']): string =>
  (trucks || []).map(formatInventoryTruckLabel).join(', ');

const getInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const InventoryLeadCard = memo(
  ({
    lead,
    index,
    status,
    contactName,
    companyName,
    columnName,
    columnColor,
    onViewDetails,
    onDelete,
  }: InventoryLeadCardProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { checkPermission } = usePermissions();
    const hasDeletePermission = checkPermission('lead_management', 'delete');
    const { users: inventoryReps } = useInventoryAssignableUsers();
    const { vehicles } = useInventory('master_inventory', undefined, {
      dontShowUpdates: true,
    });
    const activeVinSet = useMemo(() => buildActiveInventoryVinSet(vehicles), [vehicles]);

    const accent = columnColor || theme.palette.primary.main;
    const assignedRepName = lead.assignedRepId
      ? resolveInventoryRepName(inventoryReps, lead.assignedRepId)
      : null;
    const createdAt = toValidDate(lead.createdAt);
    const displayContactName = contactName || 'Unknown contact';
    const displayCompanyName = companyName || 'No company';
    const truckSummary = getTruckSummary(lead.trucks);
    const fullTruckList = getFullTruckList(lead.trucks);

    const leadNumberColor = (() => {
      if (theme.palette.mode === 'dark') return accent;
      try {
        return darken(accent, 0.2);
      } catch {
        return theme.palette.text.primary;
      }
    })();

    const handleCardClick = () => onViewDetails?.(lead);
    const stop = (handler: () => void) => (e: MouseEvent) => {
      e.stopPropagation();
      handler();
    };

    return (
      <Draggable draggableId={lead.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{ ...provided.draggableProps.style, marginBottom: 8 }}
          >
            <Paper
              variant="outlined"
              onClick={handleCardClick}
              sx={{
                p: isMobile ? 1.25 : 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                position: 'relative',
                borderLeft: `4px solid ${accent}`,
                bgcolor: snapshot.isDragging
                  ? alpha(accent, 0.08)
                  : theme.palette.background.paper,
                boxShadow: snapshot.isDragging
                  ? `0 8px 24px ${alpha(accent, 0.35)}`
                  : 'none',
                transform: snapshot.isDragging ? 'scale(1.01)' : 'none',
                transition:
                  'box-shadow 150ms ease, background-color 150ms ease, transform 150ms ease, border-color 150ms ease',
                '&:hover': {
                  boxShadow: theme.shadows[3],
                  borderColor: alpha(accent, 0.5),
                },
              }}
            >
              <Stack spacing={1.1}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Avatar
                    sx={{
                      bgcolor: alpha(accent, 0.18),
                      color: accent,
                      fontWeight: 700,
                      width: 36,
                      height: 36,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(displayContactName)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {lead.leadNumber?.trim() && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          color: leadNumberColor,
                          letterSpacing: 0.4,
                          mb: 0.25,
                        }}
                      >
                        {lead.leadNumber.trim()}
                      </Typography>
                    )}
                    <Tooltip title={displayContactName}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {displayContactName}
                      </Typography>
                    </Tooltip>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      <Business sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                        }}
                      >
                        {displayCompanyName}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Tooltip title={fullTruckList || truckSummary}>
                  <Stack spacing={0.35}>
                    {(lead.trucks || []).length === 0 ? (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <DirectionsCar sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          No trucks listed
                        </Typography>
                      </Stack>
                    ) : (
                      (lead.trucks || []).map((truck, truckIndex) => {
                        const line = formatInventoryTruckLabel(truck);
                        const inInventory = isVinInActiveInventory(truck.vin, activeVinSet)
                        return (
                          <Stack
                            key={`${truck.vin || line}-${truckIndex}`}
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            {inInventory ? (
                              <Tooltip title="In inventory">
                                <CheckCircleIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'success.main',
                                    flexShrink: 0,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <DirectionsCar
                                sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {line}
                            </Typography>
                          </Stack>
                        );
                      })
                    )}
                  </Stack>
                </Tooltip>

                {assignedRepName ? (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <PersonOutline sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Rep{' '}
                      <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {assignedRepName}
                      </Box>
                    </Typography>
                  </Stack>
                ) : null}

                {lead.note?.text?.trim() ? (
                  <Tooltip title={lead.note.text.trim()}>
                    <Stack direction="row" spacing={0.75} alignItems="flex-start">
                      <EventNoteIcon
                        sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, mt: '1px' }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                        }}
                      >
                        {lead.note.text.trim()}
                      </Typography>
                    </Stack>
                  </Tooltip>
                ) : null}

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.25 }}
                >
                  <AccessTime sx={{ fontSize: 14 }} />
                  <Typography variant="caption" noWrap>
                    In {columnName || status}{' '}
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {getTimeInStatus(lead, status)}
                    </Box>
                  </Typography>
                  {createdAt && (
                    <>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                      <Typography variant="caption" noWrap>
                        Created{' '}
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                          {formatTimeDuration(createdAt)}
                        </Box>{' '}
                        ago
                      </Typography>
                    </>
                  )}
                </Stack>

                {onDelete ? (
                  <>
                    <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.06) }} />
                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                      <Tooltip
                        title={
                          hasDeletePermission
                            ? 'Delete lead'
                            : 'You do not have permission to delete leads'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={stop(onDelete)}
                            disabled={!hasDeletePermission}
                            aria-label="Delete lead"
                            sx={{ p: 0.5 }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </>
                ) : null}
              </Stack>
            </Paper>
          </div>
        )}
      </Draggable>
    );
  },
);

InventoryLeadCard.displayName = 'InventoryLeadCard';

export default InventoryLeadCard;
